import React from 'react'
import { useDroppable } from '@dnd-kit/core'
import Stack from '@mui/material/Stack'
import { TppCell as TppCellType, TppColumn, DRAG_TYPES } from '../types'
import { useLayoutConstants } from '../hooks/useLayoutConstants'
import TppCell from './TppCell'
import { CircularProgress, SvgIcon } from '@mui/material'
import { Add } from '@mui/icons-material'

interface TppGridCellProps {
    slotId: string
    columnId: string
    cell?: TppCellType
    stickyStyle?: React.CSSProperties
    isSlotHovered?: boolean
    showOnlyHighlighted?: boolean
    isSummary: boolean
    activeDragType: string | null
    activeCellSlotId: string | null
    visibleColumns: { id: string; label: string }[]
    columnsById: Record<string, TppColumn>
    cellsById: Record<string, TppCellType>
    slotCells: Record<string, TppCellType>
    slotDragAttributes: Record<string, any>
    slotDragListeners: Record<string, any>
    creatingCellKey: string | null
    onCreateCell: (slotId: string, columnId: string) => Promise<void>
    movingCellId: string | null
    canEdit?: boolean
}

const TppGridCell: React.FC<TppGridCellProps> = ({
    slotId,
    columnId,
    cell,
    stickyStyle,
    isSlotHovered,
    showOnlyHighlighted,
    isSummary,
    activeDragType,
    activeCellSlotId,
    visibleColumns,
    columnsById,
    cellsById,
    slotCells,
    slotDragAttributes,
    slotDragListeners,
    creatingCellKey,
    onCreateCell,
    movingCellId,
    canEdit = true,
}) => {
    const layout = useLayoutConstants()
    const isCellDrag = activeDragType === DRAG_TYPES.CELL
    const isOccupied = !!cell
    const isColumnLocked = !!columnsById?.[columnId]?.locked
    const isColumnArchived = !!columnsById?.[columnId]?.archived
    const isDropDisabled = !isCellDrag || isOccupied || isColumnLocked || isColumnArchived || !canEdit
    const canCreateCell = !isColumnLocked && !isColumnArchived && canEdit

    const { setNodeRef, isOver } = useDroppable({
        id: `cell_${slotId}_${columnId}`,
        data: { type: DRAG_TYPES.DROP_ZONE, slotId, columnId },
        disabled: isDropDisabled,
    })

    const isCreatePending = creatingCellKey === `${slotId}:${columnId}`
    const style = {
        flex: '0 0 auto',
        width: layout.COLUMN_WIDTH,
        minHeight: 132.75,
        borderRight: '1px solid',
        borderColor: 'figma.grayscale.stroke',
        bgcolor: isOver && !isDropDisabled ? 'figma.grayscale.tier5' : 'transparent',
        cursor: isDropDisabled && isCellDrag ? 'not-allowed' : 'default',
        ...stickyStyle,
        ...(stickyStyle?.position === 'sticky' && !isOver ? { bgcolor: 'figma.grayscale.tier7' } : {}),
    }

    return (
        <Stack direction="column" ref={setNodeRef} p={1.5} sx={style}>
            {cell && (!showOnlyHighlighted || cell.isHighlighted) ? (
                <TppCell
                    cell={cell}
                    isSlotHovered={isSlotHovered}
                    isSummary={isSummary}
                    visibleColumns={visibleColumns}
                    columnsById={columnsById}
                    cellsById={cellsById}
                    occupiedColumnIds={Object.keys(slotCells)}
                    slotDragAttributes={slotDragAttributes}
                    slotDragListeners={slotDragListeners}
                    isMovePending={movingCellId === cell._id}
                    canEdit={canEdit}
                />
            ) : (
                <Stack
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        flex: 1,
                        border: '1px dashed',
                        borderColor: 'figma.grayscale.stroke',
                        borderRadius: '10px',
                        opacity: isCreatePending ? 1 : 0,
                        transition: 'opacity 0.15s ease',
                        cursor: isCreatePending ? 'progress' : canCreateCell ? 'pointer' : 'not-allowed',
                        pointerEvents: canCreateCell ? 'auto' : 'none',
                        ...(canCreateCell
                            ? {
                                  '&:hover': {
                                      opacity: 1,
                                  },
                              }
                            : {}),
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={async () => {
                        if (isCreatePending || !onCreateCell || !canCreateCell) return
                        await onCreateCell(slotId, columnId)
                    }}
                >
                    {isCreatePending ? (
                        <CircularProgress size={22} color="primary" />
                    ) : (
                        <SvgIcon
                            component={Add}
                            inheritViewBox
                            fontSize="small"
                            color="primary"
                            sx={{ fontSize: '22px' }}
                        />
                    )}
                </Stack>
            )}
        </Stack>
    )
}

export default TppGridCell
